import { IS_RECURRING_FIELD } from 'constants/fields';
import * as ACTION_TYPES from 'constants/index';
import {
  INTERVAL_FIELD,
  RECURRING_SINGLE_PAYMENT_OPTION_FIELD,
  UPDATE_ORDER_TEMPLATE,
} from 'constants/index';
import {
  HIDE_LANGUAGE_SELECTOR_VIEW,
  SET_CARD_ELEMENT_FLOW_ENABLED,
  SET_COUPON_ADDED,
  SET_DISABLE_PAYER_INFORMATION_FIELDS,
  SET_DISPLAY_COUPON,
  SET_DISPLAY_PAYER_INFORMATION,
  SET_PAYER_INFORMATION_COUNTRY_SUGGESTION,
  SET_THEME_SETTINGS,
  SHOW_LANGUAGE_SELECTOR_VIEW,
} from 'constants/ui';
import { createEntity } from 'factories/createEntity';
import type { Notification } from 'models';
import {
  getFieldByEntity,
  getOrderTemplate,
  getPayment,
  getPaymentOffer,
  isOnline,
  isRepeatingPayment,
} from 'selectors';

export const goToNextStep = () => ({
  type: ACTION_TYPES.GO_TO_NEXT_STEP,
});

export const goToPrevStep = () => ({
  type: ACTION_TYPES.GO_TO_PREV_STEP,
});

export const goToStep = step => ({
  type: ACTION_TYPES.GO_TO_STEP,
  payload: { step },
});

export const setFieldsValues = fields => ({
  type: ACTION_TYPES.SET_FIELDS_VALUES,
  payload: { fields },
});

export const setFieldValue = (name, value) => (dispatch, getState) => {
  const changeInPaymentInformation =
    name === 'sender_country' || name === 'amount';

  const state = getState();

  if (changeInPaymentInformation && !isRepeatingPayment(state)) {
    dispatch({
      type: ACTION_TYPES.RESET_SELECTED_OFFER,
    });
  }

  return dispatch({
    type: ACTION_TYPES.SET_FIELD_VALUE,
    payload: { name, value },
  });
};

export const resetFieldsValues = () => (dispatch, getState) => {
  const state = getState();
  const payload = getPayment(state);

  return dispatch({
    type: ACTION_TYPES.RESET_FIELDS_VALUES,
    payload,
  });
};

export const setRecipient = recipient => setFieldValue('recipient', recipient);

export const setReadOnlyFields = fields => ({
  type: ACTION_TYPES.SET_READONLY_FIELDS,
  payload: fields,
});

export const setPrefilledFields = fields => ({
  type: ACTION_TYPES.SET_PREFILLED_FIELDS,
  payload: fields,
});

export const clearOfferFields = () => (dispatch, getState) => {
  const state = getState();
  const { ids = [] } = getFieldByEntity(state, 'offers');

  dispatch({
    type: ACTION_TYPES.CLEAR_FIELDS,
    payload: [
      ...ids,
      INTERVAL_FIELD,
      RECURRING_SINGLE_PAYMENT_OPTION_FIELD,
      IS_RECURRING_FIELD,
    ],
  });
};

export const restart = () => ({
  type: ACTION_TYPES.RESTART,
});

export const newNotification = ({
  message,
  type = 'success',
}: Pick<Notification, 'message' | 'type'>) => ({
  type: ACTION_TYPES.UI_NOTIFICATION_NEW,
  payload: createEntity({ message, type }),
});

export const removeNotification = id => ({
  type: ACTION_TYPES.UI_NOTIFICATION_REMOVE,
  payload: id,
});

export const setEmbedded = () => ({
  type: ACTION_TYPES.SET_EMBEDDED_EXPERIENCE,
});

export const unsetEmbedded = () => ({
  type: ACTION_TYPES.UNSET_EMBEDDED_EXPERIENCE,
});

export const setGatewayOrigin = () => ({
  type: ACTION_TYPES.SET_GATEWAY_ORIGIN,
});

export const setGatewaySource = source => ({
  type: ACTION_TYPES.SET_GATEWAY_SOURCE,
  payload: source,
});

export const showOfflineInstructions = () => ({
  type: ACTION_TYPES.SHOW_OFFLINE_INSTRUCTIONS,
});

export const setDocCollectorFinished = (finished: boolean) => ({
  type: ACTION_TYPES.SET_DOC_COLLECTOR_FINISHED,
  payload: { finished },
});

export const setPrefilledRejectOrConfirm = status => ({
  type: ACTION_TYPES.SET_PREFILLED_REJECT_OR_CONFIRM,
  payload: { status },
});

export const hideUserManagement = () => ({
  type: ACTION_TYPES.HIDE_USER_MANAGEMENT,
});

export const setReceiveSMSNotification = receiveSMSNotification => ({
  type: ACTION_TYPES.SET_RECEIVE_SMS_NOTIFICATION,
  payload: { receiveSMSNotification },
});

export const showCancelPaymentOption = () => ({
  type: ACTION_TYPES.SHOW_CANCEL_PAYMENT_OPTION,
});

export const hideCancelPaymentOption = () => ({
  type: ACTION_TYPES.HIDE_CANCEL_PAYMENT_OPTION,
});

export const fetchEmbedded = fetching => ({
  type: ACTION_TYPES.FETCH_EMBEDDED,
  payload: fetching,
});

export const setShouldAutoLoadOnlineScript = () => (dispatch, getState) => {
  const state = getState();
  const paymentOffer = getPaymentOffer(state);

  if (!paymentOffer || !isOnline(state)) {
    return dispatch({
      type: ACTION_TYPES.SET_SHOULD_AUTO_LOAD_ONLINE_SCRIPT,
      payload: false,
    });
  }

  const isWorldpayOffer = /^worldpay/i.test(paymentOffer.paymentOption);
  const isWorldpayUnionpayOffer = /^worldpay.*unionpay/i.test(
    paymentOffer.paymentOption,
  );

  const isWorldpayWechatOffer = /^worldpay.*wechat/i.test(
    paymentOffer.paymentOption,
  );

  const shouldAutoLoadOnlineScript =
    !isWorldpayOffer || isWorldpayUnionpayOffer || isWorldpayWechatOffer;

  return dispatch({
    type: ACTION_TYPES.SET_SHOULD_AUTO_LOAD_ONLINE_SCRIPT,
    payload: shouldAutoLoadOnlineScript,
  });
};

export const setRedirectToReferrer = redirectToReferrer => ({
  type: ACTION_TYPES.SET_REDIRECT_TO_REFERRER,
  payload: { redirectToReferrer },
});

export const setPaymentProcess = () => ({
  type: ACTION_TYPES.SET_PAYMENT_PROCESS,
});

export const unsetPaymentProcess = () => ({
  type: ACTION_TYPES.UNSET_PAYMENT_PROCESS,
});

export const setTheme = theme => ({
  type: ACTION_TYPES.SET_THEME,
  payload: theme,
});

export const setTrackingPage = () => ({
  type: ACTION_TYPES.SET_TRACKING_PAGE,
});

export const unsetTrackingPage = () => ({
  type: ACTION_TYPES.UNSET_TRACKING_PAGE,
});

export const setChatVisible = () => ({
  type: ACTION_TYPES.SET_CHAT_VISIBLE,
});

export const setMaxAmount = amount => ({
  type: ACTION_TYPES.SET_MAX_AMOUNT,
  payload: amount,
});

export const setDisplayPayerInformation = payload => dispatch =>
  dispatch({ type: SET_DISPLAY_PAYER_INFORMATION, payload });

export const setRecurringConfigurationId = payload => dispatch =>
  dispatch({ type: ACTION_TYPES.SET_RECURRING_CONFIGURATION_ID, payload });

export const setShouldDisablePayerInformationFields = payload => dispatch =>
  dispatch({ type: SET_DISABLE_PAYER_INFORMATION_FIELDS, payload });

export const setThemeSettings =
  (payload = {}) =>
  dispatch =>
    dispatch({ type: SET_THEME_SETTINGS, payload });

export const setPayerInformationCountrySuggestion = payload => dispatch =>
  dispatch({ type: SET_PAYER_INFORMATION_COUNTRY_SUGGESTION, payload });

export const updateOrderTemplate = payload => {
  return {
    type: UPDATE_ORDER_TEMPLATE,
    payload,
  };
};
export const setFieldsValuesFromOrderTemplate =
  () => async (dispatch, getState) => {
    const state = getState();
    const orderTemplate = getOrderTemplate(state);
    const fields = {};
    const readOnlyFields = [];
    if (orderTemplate.id) {
      orderTemplate.sender?.fields?.forEach(val => {
        const fieldId = `sender_${val.id}`;
        fields[fieldId] = val.value;
        if (val.readOnly) {
          readOnlyFields.push(fieldId);
        }
      });
      dispatch(setReadOnlyFields(readOnlyFields));
      dispatch(setFieldsValues(fields));
    }
  };

export const setDisplayCoupon = payload => dispatch =>
  dispatch({ type: SET_DISPLAY_COUPON, payload });

export const setCouponAdded = payload => dispatch =>
  dispatch({ type: SET_COUPON_ADDED, payload });

export const showLanguageSelectorView = () => ({
  type: SHOW_LANGUAGE_SELECTOR_VIEW,
});

export const hideLanguageSelectorView = () => ({
  type: HIDE_LANGUAGE_SELECTOR_VIEW,
});

export const setCardElementFlowEnabled = (enabled: boolean) => ({
  type: SET_CARD_ELEMENT_FLOW_ENABLED,
  payload: enabled,
});
